import React, {Dispatch, SetStateAction, useMemo, useState} from 'react'
import {
  Badge,
  Button,
  ButtonGroup,
  Checkbox,
  DateTimeRangePicker,
  Icon,
  Text,
} from '@therms/atalaya'
import {MdManageSearch} from 'react-icons/md'
import {IoClose} from 'react-icons/io5'
import {EventsSearchFiltersModal} from '@src/modules/Events/router/screens/Index/components/EventsSearchFiltersModal.tsx'

interface EventsSearchFiltersProps {
  assignedToUserFilter: boolean
  filtersState: {
    selectedActivityTypeIds?: string[] | undefined
    selectedDateTimeRange?: {start?: string; end?: string}
    selectedEventTypeIds?: string[] | undefined
    selectedLocationId?: string | undefined
    selectedUserIds?: string[] | undefined
  }
  searchText: string
  setAssignedToUserFilter: Dispatch<SetStateAction<boolean>>
  setFiltersState: (
    val: Partial<{
      selectedActivityTypeIds: string[] | undefined
      selectedDateTimeRange: {start?: string; end?: string}
      selectedEventTypeIds: string[] | undefined
      selectedLocationId: string | undefined
      selectedUserIds: string[] | undefined
    }>,
  ) => void
  setSearchText: Dispatch<SetStateAction<string>>
}

function EventsSearchFilters({
  assignedToUserFilter,
  filtersState,
  searchText,
  setAssignedToUserFilter,
  setFiltersState,
  setSearchText,
}: EventsSearchFiltersProps) {
  const [showAdvanceFilters, setAdvanceFilters] = useState(false)

  const filterCount = useMemo(
    () =>
      (filtersState.selectedActivityTypeIds?.length || 0) +
      (filtersState.selectedUserIds?.length || 0) +
      (filtersState.selectedEventTypeIds?.length || 0) +
      (filtersState.selectedLocationId ? 1 : 0) +
      (filtersState.selectedDateTimeRange?.start ? 1 : 0),
    [filtersState],
  )

  return (
    <div className="space-y-base">
      <div className="gap-xxs flex flex-wrap items-center justify-start">
        <div className="">
          <Text
            placeholder="Search..."
            onChangeValue={setSearchText}
            value={searchText}
            search
          />
        </div>

        <div className="flex min-w-fit flex-nowrap">
          <DateTimeRangePicker
            placeholder="Date/Time Range"
            onChangeValue={(val) => {
              setFiltersState({
                selectedDateTimeRange: val,
              })
            }}
            onClearValue={() =>
              setFiltersState({
                selectedDateTimeRange: {
                  end: undefined,
                  start: undefined,
                },
              })
            }
            value={filtersState.selectedDateTimeRange}
          />
        </div>

        <div className="flex items-center justify-start">
          <Checkbox
            active={assignedToUserFilter}
            name="userFilter"
            onChangeValue={setAssignedToUserFilter}
            label={<span className="truncate">Assigned to me</span>}
          />
        </div>

        <div className="min-w-fit flex-nowrap">
          <ButtonGroup>
            {filterCount ? (
              <Button
                onClick={() => {
                  setAdvanceFilters(!showAdvanceFilters)
                }}
                size="base"
                subtle
                variant="info"
              >
                <span className="pr-xxs">
                  <Badge value={filterCount} variant="info" /> Filters Applied
                </span>

                <Icon Icon={MdManageSearch} />
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setAdvanceFilters(!showAdvanceFilters)
                }}
                size="base"
                subtle
                variant="neutral"
              >
                <span className="pr-xxs">Filters</span>

                <Icon Icon={MdManageSearch} />
              </Button>
            )}

            {(assignedToUserFilter ||
              !!filtersState.selectedEventTypeIds?.length ||
              filtersState.selectedLocationId ||
              filtersState.selectedDateTimeRange?.start ||
              filtersState.selectedDateTimeRange?.end ||
              !!filtersState.selectedActivityTypeIds?.length ||
              !!filtersState.selectedUserIds?.length) && (
              <Button
                onClick={() => {
                  setAdvanceFilters(false)
                  setAssignedToUserFilter(false)
                  setFiltersState({
                    selectedActivityTypeIds: undefined,
                    selectedDateTimeRange: {end: undefined, start: undefined},
                    selectedEventTypeIds: undefined,
                    selectedLocationId: '',
                    selectedUserIds: undefined,
                  })
                }}
                subtle
              >
                <Icon color="critical" Icon={IoClose} /> Clear
              </Button>
            )}
          </ButtonGroup>
        </div>
      </div>

      {showAdvanceFilters && (
        <EventsSearchFiltersModal
          filtersState={filtersState}
          setFiltersState={setFiltersState}
          onClose={() => setAdvanceFilters(false)}
        />
      )}
    </div>
  )
}

export {EventsSearchFilters}
